'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/ml'

const KMeansViz = dynamic(() => import('../_components/widgets/MLWidgets').then(m => m.KMeansViz), { ssr: false })

export default function Clustering() {
  return (
    <Lesson>
      <Step>
        <H>No answers, just data</H>
        <P>
          Everything so far was <strong>supervised</strong>: every example came with the right answer. Often you have no
          answers at all — just a pile of data — and you want to discover its structure. Which customers behave alike? Which news
          stories are about the same event? Which colours appear in this photo?
        </P>
        <P>
          The classic tool is <strong>k-means clustering</strong>. You choose how many groups you want (<em>k</em>), and it
          alternates two simple steps until nothing changes:
        </P>
        <ol className="gd-list">
          <li>
            <strong>Assign:</strong> every point joins the group whose centre (×) is nearest.
          </li>
          <li>
            <strong>Update:</strong> every centre moves to the average position of its group.
          </li>
        </ol>
      </Step>

      <Step gate="q-k">
        <H>Step through it</H>
        <Figure n="6.10" caption="Alternate the two steps by hand. The coloured regions show which centre owns each part of the plane. Try different k, and different random starts.">
          <KMeansViz />
        </Figure>
        <Quiz
          id="q-k"
          question="You run k-means twice on the same data, with different random starting centres, and get different groupings. Why?"
          options={[
            { label: 'k-means only goes downhill from where it starts, so it can settle into different local optima.', correct: true },
            { label: 'There’s a bug — k-means should always give the same answer.', why: 'It’s guaranteed to improve every round, but not to find the single best grouping.' },
            { label: 'The data changed between runs.', why: 'Same data — only the starting centres differed.' },
          ]}
          explain="Same lesson as gradient descent: where you start can decide which valley you end up in. In practice people run k-means several times and keep the best result."
        />
      </Step>

      <Step gate="kmeans">
        <H>Build k-means</H>
        <Exercise
          id="kmeans"
          title="assign and update"
          {...EX.kmeans}
          hints={[
            <>
              For each point, loop over the centroids and keep the index with the smallest <C>dist2</C>.
            </>,
            <>
              For centroid <C>c</C>, gather <C>{'points.filter((_, i) => labels[i] === c)'}</C> and average their x’s and y’s.
            </>,
          ]}
        />
      </Step>

      <Step>
        <H>Where this shows up</H>
        <P>
          The total squared distance (the readout under the figure) is k-means’ loss, and each step can only lower it. It’s a
          close cousin of everything else in this module: a model, a loss, and an algorithm that improves it.
        </P>
        <Callout kind="why">
          Modern AI mostly learns from unlabelled data too — but in a cleverer way called <strong>self-supervised</strong>{' '}
          learning, where the data supplies its own answers (“predict the next word”). You’ll see it in Module 8. First, the most
          important model of all: the neural network.
        </Callout>
      </Step>
    </Lesson>
  )
}
